import {
  createRouter,
  createRootRoute,
  createRoute,
  Outlet,
  useNavigate,
  useRouterState,
  useParams,
  redirect,
} from '@tanstack/react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useEffect, type ReactNode } from 'react';
import { AuthProvider, useAuth } from '@/lib/auth';
import { SupportProvider } from '@/lib/support-context';
import { SupportWidget } from '@/components/support-widget';
import { UnsavedProvider } from '@/lib/unsaved';
import { RootRedirect } from '@/pages/root-redirect';
import { SignupPage, LoginPage, VerifyEmailPage, ForgotPasswordPage, ResetPasswordPage } from '@/pages/auth';
import {
  AppHomePage,
  ReviewStartPage,
  ReviewDetailPage,
  ImprovementsPage,
  QuoteRequestsPage,
  ReviewsProofPage,
  SocialPage,
  SettingsPage,
} from '@/pages/kit-placeholders';
import { ShareReportPage } from '@/pages/review';
import { PreviewReportPage } from '@/pages/preview-report';
import { AdminPage } from '@/pages/admin';
import { OnboardingPage } from '@/pages/onboarding';
import { AppShell } from '@/components/layout/app-shell';
import { NotFoundPage, SessionExpiredPage, ServerErrorPage } from '@/pages/error-pages';
import { HowItWorksPage, SampleReportPage, BotPage, ContactPage, RemoveReportPage } from '@/pages/public-pages';
import {
  TermsPage,
  PrivacyPage,
  RefundsPage,
  CookiesPage,
  AcceptableUsePage,
  DisclaimerPage,
  SubprocessorsPage,
  SecurityPage,
  SmsTermsPage,
  DpaPage,
  AccessibilityPage,
  DataRequestsPage,
} from '@/pages/legal';
import { AppShell as LegacyAppShell } from '@/legacy/components/layout/app-shell';
import { OnboardingPage as LegacyOnboardingPage } from '@/legacy/pages/onboarding';
import { DashboardPage } from '@/legacy/pages/dashboard';
import { RetentionPage } from '@/legacy/pages/retention';
import { CampaignsPage } from '@/legacy/pages/campaigns';
import { OpportunitiesPage } from '@/legacy/pages/opportunities';
import { UpgradesPage } from '@/legacy/pages/upgrades';
import { InsightsPage } from '@/legacy/pages/insights';
import { AgreementsPage, NewAgreementPage } from '@/legacy/pages/agreements';
import { PayPage } from '@/legacy/pages/pay';
import { UnsubscribePage } from '@/legacy/pages/unsubscribe';
import { CustomersPage, NewCustomerPage, CustomerDetailPage } from '@/legacy/pages/customers';
import { RemindersPage } from '@/legacy/pages/reminders';
import {
  SettingsPage as LegacySettingsPage,
  PaymentsPage,
  TemplatesPage,
  HelpPage,
  BillingPage,
} from '@/legacy/pages/settings';
import { IntegrationsPage } from '@/legacy/pages/settings/integrations';
import { legacyRenewalEnabledClient } from '@maintenance-tracker/shared';
import { captureReferralParams } from '@/lib/referral';

const LEGACY_ON = legacyRenewalEnabledClient();

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 30_000, retry: 1 } },
});

function ReferralCapture({ children }: { children: ReactNode }) {
  useEffect(() => {
    captureReferralParams();
  }, []);
  return <>{children}</>;
}

function ProtectedLayout() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate({ to: '/login', replace: true });
      return;
    }
    if (LEGACY_ON && !user.settings?.onboardingComplete && !pathname.startsWith('/onboarding')) {
      navigate({ to: '/onboarding', replace: true });
      return;
    }
    if (
      !LEGACY_ON &&
      !user.settings?.onboardingComplete &&
      !pathname.startsWith('/onboarding') &&
      !pathname.startsWith('/verify-email')
    ) {
      // Allow /app with Finish setup card; force onboarding only for brand-new empty names optionally
    }
  }, [user, loading, pathname, navigate]);

  if (loading) {
    return <div className="flex min-h-screen items-center justify-center text-text-secondary">Loading…</div>;
  }
  if (!user) return null;
  if (LEGACY_ON && !user.settings?.onboardingComplete && !pathname.startsWith('/onboarding')) return null;

  if (!LEGACY_ON) {
    if (pathname.startsWith('/onboarding')) {
      return <Outlet />;
    }
    return (
      <UnsavedProvider>
        <AppShell>
          <Outlet />
        </AppShell>
      </UnsavedProvider>
    );
  }

  return (
    <LegacyAppShell>
      <Outlet />
    </LegacyAppShell>
  );
}

function redirectToApp(): never {
  throw redirect({ to: '/app' });
}

const rootRoute = createRootRoute({
  component: () => (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <SupportProvider>
          <ReferralCapture>
            <Outlet />
            <SupportWidget />
          </ReferralCapture>
        </SupportProvider>
      </AuthProvider>
    </QueryClientProvider>
  ),
  errorComponent: ({ error, reset }) => (
    <ServerErrorPage
      onRetry={() => {
        reset();
        window.location.reload();
      }}
    />
  ),
});

const publicRoutes = [
  createRoute({ getParentRoute: () => rootRoute, path: '/', component: RootRedirect }),
  createRoute({ getParentRoute: () => rootRoute, path: '/signup', component: SignupPage }),
  createRoute({ getParentRoute: () => rootRoute, path: '/login', component: LoginPage }),
  createRoute({ getParentRoute: () => rootRoute, path: '/verify-email', component: VerifyEmailPage }),
  createRoute({ getParentRoute: () => rootRoute, path: '/forgot-password', component: ForgotPasswordPage }),
  createRoute({ getParentRoute: () => rootRoute, path: '/reset-password', component: ResetPasswordPage }),
  createRoute({ getParentRoute: () => rootRoute, path: '/privacy', component: PrivacyPage }),
  createRoute({ getParentRoute: () => rootRoute, path: '/terms', component: TermsPage }),
  createRoute({ getParentRoute: () => rootRoute, path: '/how-it-works', component: HowItWorksPage }),
  createRoute({ getParentRoute: () => rootRoute, path: '/sample-report', component: SampleReportPage }),
  createRoute({ getParentRoute: () => rootRoute, path: '/bot', component: BotPage }),
  createRoute({ getParentRoute: () => rootRoute, path: '/contact', component: ContactPage }),
  createRoute({ getParentRoute: () => rootRoute, path: '/remove-report', component: RemoveReportPage }),
  createRoute({ getParentRoute: () => rootRoute, path: '/session-expired', component: SessionExpiredPage }),
  createRoute({ getParentRoute: () => rootRoute, path: '/r/$code', component: PreviewReportPage }),
  createRoute({ getParentRoute: () => rootRoute, path: '/share/$token', component: ShareReportPage }),
  createRoute({ getParentRoute: () => rootRoute, path: '/admin', component: AdminPage }),
];

const protectedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'protected',
  component: ProtectedLayout,
});

const LEGACY_REDIRECT_PATHS = [
  '/dashboard',
  '/retention',
  '/campaigns',
  '/opportunities',
  '/upgrades',
  '/insights',
  '/agreements',
  '/agreements/new',
  '/customers',
  '/customers/new',
  '/customers/$id',
  '/reminders',
  '/settings',
  '/settings/billing',
  '/settings/payments',
  '/settings/integrations',
  '/settings/templates',
  '/integrations',
  '/help',
  '/pay/$token',
  '/unsubscribe',
  '/refunds',
  '/cookies',
  '/acceptable-use',
  '/disclaimer',
  '/subprocessors',
  '/security',
  '/sms-terms',
  '/dpa',
  '/accessibility',
  '/data-requests',
  '/blog',
  '/blog/$slug',
  '/for/$niche',
] as const;

const legacyPublicRoutes = [
  createRoute({ getParentRoute: () => rootRoute, path: '/refunds', component: RefundsPage }),
  createRoute({ getParentRoute: () => rootRoute, path: '/cookies', component: CookiesPage }),
  createRoute({ getParentRoute: () => rootRoute, path: '/acceptable-use', component: AcceptableUsePage }),
  createRoute({ getParentRoute: () => rootRoute, path: '/disclaimer', component: DisclaimerPage }),
  createRoute({ getParentRoute: () => rootRoute, path: '/subprocessors', component: SubprocessorsPage }),
  createRoute({ getParentRoute: () => rootRoute, path: '/security', component: SecurityPage }),
  createRoute({ getParentRoute: () => rootRoute, path: '/sms-terms', component: SmsTermsPage }),
  createRoute({ getParentRoute: () => rootRoute, path: '/dpa', component: DpaPage }),
  createRoute({ getParentRoute: () => rootRoute, path: '/accessibility', component: AccessibilityPage }),
  createRoute({ getParentRoute: () => rootRoute, path: '/data-requests', component: DataRequestsPage }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/pay/$token',
    component: function PayRoute() {
      return <PayPage />;
    },
  }),
  createRoute({ getParentRoute: () => rootRoute, path: '/unsubscribe', component: UnsubscribePage }),
];

const legacyProtectedRoutes = [
  createRoute({ getParentRoute: () => protectedRoute, path: '/onboarding', component: LegacyOnboardingPage }),
  createRoute({ getParentRoute: () => protectedRoute, path: '/dashboard', component: DashboardPage }),
  createRoute({ getParentRoute: () => protectedRoute, path: '/retention', component: RetentionPage }),
  createRoute({ getParentRoute: () => protectedRoute, path: '/campaigns', component: CampaignsPage }),
  createRoute({ getParentRoute: () => protectedRoute, path: '/opportunities', component: OpportunitiesPage }),
  createRoute({ getParentRoute: () => protectedRoute, path: '/upgrades', component: UpgradesPage }),
  createRoute({ getParentRoute: () => protectedRoute, path: '/insights', component: InsightsPage }),
  createRoute({ getParentRoute: () => protectedRoute, path: '/agreements', component: AgreementsPage }),
  createRoute({ getParentRoute: () => protectedRoute, path: '/agreements/new', component: NewAgreementPage }),
  createRoute({ getParentRoute: () => protectedRoute, path: '/customers', component: CustomersPage }),
  createRoute({ getParentRoute: () => protectedRoute, path: '/customers/new', component: NewCustomerPage }),
  createRoute({
    getParentRoute: () => protectedRoute,
    path: '/customers/$id',
    component: function CustomerDetailRoute() {
      const { id } = useParams({ strict: false }) as { id: string };
      return <CustomerDetailPage id={id} />;
    },
  }),
  createRoute({ getParentRoute: () => protectedRoute, path: '/reminders', component: RemindersPage }),
  createRoute({ getParentRoute: () => protectedRoute, path: '/settings', component: LegacySettingsPage }),
  createRoute({ getParentRoute: () => protectedRoute, path: '/settings/billing', component: BillingPage }),
  createRoute({ getParentRoute: () => protectedRoute, path: '/settings/payments', component: PaymentsPage }),
  createRoute({
    getParentRoute: () => protectedRoute,
    path: '/integrations',
    beforeLoad: () => {
      throw redirect({ to: '/settings/integrations' });
    },
  }),
  createRoute({ getParentRoute: () => protectedRoute, path: '/settings/integrations', component: IntegrationsPage }),
  createRoute({ getParentRoute: () => protectedRoute, path: '/settings/templates', component: TemplatesPage }),
  createRoute({ getParentRoute: () => protectedRoute, path: '/help', component: HelpPage }),
  createRoute({
    getParentRoute: () => protectedRoute,
    path: '/app',
    beforeLoad: () => {
      throw redirect({ to: '/dashboard' });
    },
  }),
];

const growthProtectedRoutes = [
  createRoute({ getParentRoute: () => protectedRoute, path: '/onboarding', component: OnboardingPage }),
  createRoute({ getParentRoute: () => protectedRoute, path: '/app', component: AppHomePage }),
  createRoute({ getParentRoute: () => protectedRoute, path: '/app/review', component: ReviewStartPage }),
  createRoute({
    getParentRoute: () => protectedRoute,
    path: '/app/review/$id',
    component: ReviewDetailPage,
  }),
  createRoute({ getParentRoute: () => protectedRoute, path: '/app/improvements', component: ImprovementsPage }),
  createRoute({ getParentRoute: () => protectedRoute, path: '/app/quote-requests', component: QuoteRequestsPage }),
  createRoute({ getParentRoute: () => protectedRoute, path: '/app/reviews-proof', component: ReviewsProofPage }),
  createRoute({ getParentRoute: () => protectedRoute, path: '/app/social', component: SocialPage }),
  createRoute({ getParentRoute: () => protectedRoute, path: '/app/settings', component: SettingsPage }),
];

const legacyOffRedirects = LEGACY_REDIRECT_PATHS.map((path) =>
  createRoute({
    getParentRoute: () => rootRoute,
    path,
    beforeLoad: redirectToApp,
  }),
);

const notFoundRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '*',
  component: NotFoundPage,
});

const routeTree = rootRoute.addChildren([
  ...publicRoutes,
  ...(LEGACY_ON ? legacyPublicRoutes : []),
  protectedRoute.addChildren(LEGACY_ON ? legacyProtectedRoutes : growthProtectedRoutes),
  ...(LEGACY_ON ? [] : legacyOffRedirects),
  notFoundRoute,
]);

export const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
